import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  Select,
  SelectItem,
  SelectContent,
  SelectTrigger,
  SelectValue,
} from "@src/lib/ui/Select";
import { cn } from "@src/lib/utils/cn";
import { Input } from "@src/lib/ui/Input";
import {
  LibraryItemType,
  TemperatureUnit,
  TriggerInput,
  VerificationStepType,
  NumberTriggerCondition,
} from "@src/types.generated";
import Text from "@src/lib/ui/Text";
import { SkillBuilder_VerificationStepFragment } from "./SkillDetailPage.generated";
import { TEMPERATURE_UNIT } from "./utils/utils";
import { MultiSelect } from "@src/lib/ui/MultiSelect";

export const NUMBER_CONDITION_INPUT_MAX = 999999999;
export const NUMBER_CONDITION_INPUT_MIN = -999999999;
export const SELECT_WIDTH = "w-[160px]";

export const NUMBER_TRIGGER_CONDITION_COPY = {
  [NumberTriggerCondition.GreaterThan]: "greater than",
  [NumberTriggerCondition.LowerThan]: "lower than",
  [NumberTriggerCondition.EqualTo]: "equal to",
  [NumberTriggerCondition.NotEqual]: "not equal to",
  [NumberTriggerCondition.OutsideRange]: "outside a range of",
};

export const NUMBER_TRIGGER_CONDITION_OPTIONS = [
  NumberTriggerCondition.GreaterThan,
  NumberTriggerCondition.LowerThan,
  NumberTriggerCondition.OutsideRange,
  NumberTriggerCondition.EqualTo,
  NumberTriggerCondition.NotEqual,
];

export const TEMPERATURE_TRIGGER_CONDITION_OPTIONS = [
  NumberTriggerCondition.GreaterThan,
  NumberTriggerCondition.LowerThan,
  NumberTriggerCondition.OutsideRange,
];

// Allows intermediate typing states like "", "-", "5." and "-5.2"
const NUMBER_TEXT_PATTERN = /^-?\d*\.?\d*$/;

const formatNumberValue = (value: number | null | undefined): string =>
  value == null || Number.isNaN(value) ? "" : String(value);

// Submit-time validation for number conditions. Mirrors the backend
// `number_range_top_must_be_greater_than_number` DB constraint — callers must
// block the mutation and surface the message when this returns non-null.
export const getNumberConditionError = (
  conditionInput: ConditionFields,
): string | null => {
  const numberCondition = conditionInput.numberCondition;
  if (!numberCondition) {
    return null;
  }
  const bottom = numberCondition.number.value;
  if (bottom == null || Number.isNaN(bottom)) {
    return "Enter a number for the condition.";
  }
  const values = [bottom];
  if (numberCondition.condition === NumberTriggerCondition.OutsideRange) {
    const top = numberCondition.numberRangeTop?.value;
    if (top == null || Number.isNaN(top)) {
      return "Enter both range values.";
    }
    if (top <= bottom) {
      return "The end of the range must be greater than the start.";
    }
    values.push(top);
  }
  if (
    values.some(
      (value) =>
        value < NUMBER_CONDITION_INPUT_MIN ||
        value > NUMBER_CONDITION_INPUT_MAX,
    )
  ) {
    return `Values must be between ${NUMBER_CONDITION_INPUT_MIN} and ${NUMBER_CONDITION_INPUT_MAX}.`;
  }
  return null;
};

// The server's conditional_step_group_require_exactly_one_condition constraint
// rejects a group with no condition, and apply_builder_actions is atomic — one
// such action fails the whole batch. Callers must block submit when this
// returns false.
export const hasConditionSet = (conditionInput: ConditionFields): boolean =>
  Boolean(
    conditionInput.numberCondition ||
      conditionInput.yesNoCondition ||
      conditionInput.checkedCondition ||
      conditionInput.verificationStepMultipleChoiceCondition ||
      conditionInput.verificationStepMultipleChoiceAnyCondition,
  );

export type ConditionFields = {
  numberCondition?: TriggerInput["numberCondition"];
  yesNoCondition?: TriggerInput["yesNoCondition"];
  checkedCondition?: TriggerInput["checkedCondition"];
  verificationStepMultipleChoiceCondition?: TriggerInput["verificationStepMultipleChoiceCondition"];
  verificationStepMultipleChoiceAnyCondition?: TriggerInput["verificationStepMultipleChoiceAnyCondition"];
};

type SelectConditionProps<T extends ConditionFields> = {
  conditionInput: T;
  setConditionInput: (input: T) => void;
  step?: SkillBuilder_VerificationStepFragment;
  libraryItemType?: LibraryItemType;
  showIsAnyOf?: boolean;
};

const SelectCondition = <T extends ConditionFields>({
  conditionInput,
  setConditionInput,
  step,
  libraryItemType,
  showIsAnyOf,
}: SelectConditionProps<T>): React.ReactElement | null => {
  const activeStep = step;
  const verificationStepType = activeStep?.type;
  switch (verificationStepType) {
    case VerificationStepType.Number:
      return (
        <SelectNumberCondition
          conditionInput={conditionInput}
          setConditionInput={setConditionInput}
          step={activeStep}
        />
      );
    case VerificationStepType.Temperature:
      return (
        <SelectNumberCondition
          conditionInput={conditionInput}
          setConditionInput={setConditionInput}
          step={activeStep}
        />
      );
    case VerificationStepType.YesNo:
      return (
        <SelectYesNoCondition
          conditionInput={conditionInput}
          setConditionInput={setConditionInput}
        />
      );
    case VerificationStepType.Checkbox:
      return (
        <SelectCheckboxCondition
          conditionInput={conditionInput}
          setConditionInput={setConditionInput}
          libraryItemType={libraryItemType}
        />
      );
    case VerificationStepType.MultipleChoice:
      return (
        <SelectMultipleChoiceCondition
          conditionInput={conditionInput}
          setConditionInput={setConditionInput}
          step={activeStep}
          libraryItemType={libraryItemType}
          showIsAnyOf={showIsAnyOf}
        />
      );
    default:
      return null;
  }
};

const SelectNumberCondition = <T extends ConditionFields>({
  conditionInput,
  setConditionInput,
  step,
}: SelectConditionProps<T>) => {
  const numberCondition = conditionInput.numberCondition;
  const temperatureUnit = step?.temperatureUnit || TemperatureUnit.Fahrenheit;
  const isTemperature = step?.type === VerificationStepType.Temperature;
  const showTemperatureUnit = isTemperature && temperatureUnit;
  const conditionOptions = isTemperature
    ? TEMPERATURE_TRIGGER_CONDITION_OPTIONS
    : NUMBER_TRIGGER_CONDITION_OPTIONS;
  const onConditionChange = useCallback(
    (value: string) => {
      if (!numberCondition) {
        return;
      }
      let numberRangeTop = numberCondition.numberRangeTop;
      if (
        value === NumberTriggerCondition.OutsideRange &&
        numberCondition.condition !== NumberTriggerCondition.OutsideRange
      ) {
        numberRangeTop = { value: numberCondition.number.value + 1 };
      } else if (value !== NumberTriggerCondition.OutsideRange) {
        numberRangeTop = undefined;
      }
      setConditionInput({
        ...conditionInput,
        numberCondition: {
          ...numberCondition,
          condition: value as NumberTriggerCondition,
          numberRangeTop,
        },
      });
    },
    [setConditionInput, conditionInput, numberCondition],
  );
  // Local text state so the user can type freely (including intermediate
  // states like "-", "5." or a moment where top <= bottom). The parsed value
  // propagates on every valid keystroke; range validity is only enforced at
  // submit via getNumberConditionError.
  const numberValue = numberCondition?.number.value;
  const numberRangeTopValue = numberCondition?.numberRangeTop?.value;
  const [numberText, setNumberText] = useState(() =>
    formatNumberValue(numberValue),
  );
  const [numberRangeTopText, setNumberRangeTopText] = useState(() =>
    formatNumberValue(numberRangeTopValue),
  );
  // Track the last values this component propagated so external updates
  // (condition switch, dialog reopen) re-sync the text without fighting typing.
  const lastPropagated = useRef({
    number: numberValue,
    rangeTop: numberRangeTopValue,
  });
  useEffect(() => {
    if (numberValue !== lastPropagated.current.number) {
      lastPropagated.current.number = numberValue;
      setNumberText(formatNumberValue(numberValue));
    }
    if (numberRangeTopValue !== lastPropagated.current.rangeTop) {
      lastPropagated.current.rangeTop = numberRangeTopValue;
      setNumberRangeTopText(formatNumberValue(numberRangeTopValue));
    }
  }, [numberValue, numberRangeTopValue]);

  const onNumberChange = useCallback(
    (raw: string) => {
      if (!numberCondition || !NUMBER_TEXT_PATTERN.test(raw)) {
        return;
      }
      setNumberText(raw);
      const parsed = Number(raw);
      if (raw === "" || Number.isNaN(parsed)) {
        return;
      }
      lastPropagated.current.number = parsed;
      setConditionInput({
        ...conditionInput,
        numberCondition: {
          ...numberCondition,
          number: { value: parsed },
        },
      });
    },
    [setConditionInput, conditionInput, numberCondition],
  );
  const onNumberRangeTopChange = useCallback(
    (raw: string) => {
      if (!numberCondition || !NUMBER_TEXT_PATTERN.test(raw)) {
        return;
      }
      setNumberRangeTopText(raw);
      const parsed = Number(raw);
      if (raw === "" || Number.isNaN(parsed)) {
        return;
      }
      lastPropagated.current.rangeTop = parsed;
      setConditionInput({
        ...conditionInput,
        numberCondition: {
          ...numberCondition,
          numberRangeTop: { value: parsed },
        },
      });
    },
    [setConditionInput, conditionInput, numberCondition],
  );
  const onNumberBlur = useCallback(() => {
    setNumberText(formatNumberValue(numberValue));
  }, [numberValue]);
  const onNumberRangeTopBlur = useCallback(() => {
    setNumberRangeTopText(formatNumberValue(numberRangeTopValue));
  }, [numberRangeTopValue]);

  if (!numberCondition) return null;

  return (
    <div className="flex items-center gap-[12px]">
      <Select
        onValueChange={(value) =>
          onConditionChange(value as NumberTriggerCondition)
        }
        value={numberCondition.condition}
      >
        <SelectTrigger className="flex-1">
          <SelectValue>
            {NUMBER_TRIGGER_CONDITION_COPY[numberCondition.condition]}
          </SelectValue>
        </SelectTrigger>
        <SelectContent portal={false}>
          {conditionOptions.map((option) => (
            <SelectItem key={option} value={option}>
              {NUMBER_TRIGGER_CONDITION_COPY[option]}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      <div className="flex flex-1 items-center gap-[12px]">
        <div className="relative flex-1">
          <Input
            value={numberText}
            onChange={(e) => onNumberChange(e.target.value)}
            onBlur={onNumberBlur}
            inputClassName={cn(showTemperatureUnit && "pr-[27px]", "w-full")}
            type="number"
            min={NUMBER_CONDITION_INPUT_MIN}
            max={NUMBER_CONDITION_INPUT_MAX}
          />
          {showTemperatureUnit && (
            <Text
              type="P2"
              className="absolute top-1/2 right-[12px] -translate-y-1/2 text-gray-3"
              fontWeight="SemiBold"
            >
              {TEMPERATURE_UNIT[temperatureUnit || TemperatureUnit.Fahrenheit]}
            </Text>
          )}
        </div>
        {numberCondition.condition === NumberTriggerCondition.OutsideRange && (
          <>
            <Text type="P2" fontWeight="Medium">
              to
            </Text>
            <div className="relative flex flex-1">
              <Input
                value={numberRangeTopText}
                onChange={(e) => onNumberRangeTopChange(e.target.value)}
                onBlur={onNumberRangeTopBlur}
                inputClassName={cn(
                  showTemperatureUnit && "pr-[27px]",
                  "w-full",
                )}
                type="number"
                min={NUMBER_CONDITION_INPUT_MIN}
                max={NUMBER_CONDITION_INPUT_MAX}
              />
              {showTemperatureUnit && (
                <Text
                  type="P2"
                  className="absolute top-1/2 right-[12px] -translate-y-1/2 text-gray-3"
                  fontWeight="SemiBold"
                >
                  {
                    TEMPERATURE_UNIT[
                      temperatureUnit || TemperatureUnit.Fahrenheit
                    ]
                  }
                </Text>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

const SelectYesNoCondition = <T extends ConditionFields>({
  conditionInput,
  setConditionInput,
}: Omit<SelectConditionProps<T>, "step">) => {
  const onConditionChange = useCallback(
    (value: string) => {
      setConditionInput({
        ...conditionInput,
        yesNoCondition: { value: value === "yes" },
      });
    },
    [setConditionInput, conditionInput],
  );
  return (
    <div className="flex items-center">
      <Text type="P2" fontWeight="Medium" className="mr-[12px]">
        is
      </Text>
      <div className={SELECT_WIDTH}>
        <Select
          onValueChange={onConditionChange}
          value={conditionInput.yesNoCondition?.value ? "yes" : "no"}
        >
          <SelectTrigger>
            <SelectValue>
              {conditionInput.yesNoCondition?.value ? "Yes" : "No"}
            </SelectValue>
          </SelectTrigger>
          <SelectContent portal={false}>
            <SelectItem value="no">No</SelectItem>
            <SelectItem value="yes">Yes</SelectItem>
          </SelectContent>
        </Select>
      </div>
    </div>
  );
};

const SelectCheckboxCondition = <T extends ConditionFields>({
  conditionInput,
  setConditionInput,
  libraryItemType,
}: Omit<SelectConditionProps<T>, "step">) => {
  const onConditionChange = useCallback(
    (value: string) => {
      setConditionInput({
        ...conditionInput,
        checkedCondition: { value: value === "checked" },
      });
    },
    [setConditionInput, conditionInput],
  );
  const isTaskList = libraryItemType === LibraryItemType.Task;
  return (
    <div className="flex items-center">
      <Text type="P2" fontWeight="Medium" className="mr-[12px]">
        is
      </Text>
      <div className={SELECT_WIDTH}>
        <Select
          disabled={isTaskList}
          onValueChange={onConditionChange}
          value={
            conditionInput.checkedCondition?.value ? "checked" : "unchecked"
          }
        >
          <SelectTrigger>
            <SelectValue>
              {conditionInput.checkedCondition?.value ? "Checked" : "Unchecked"}
            </SelectValue>
          </SelectTrigger>
          <SelectContent portal={false}>
            <SelectItem value="unchecked">Unchecked</SelectItem>
            <SelectItem value="checked">Checked</SelectItem>
          </SelectContent>
        </Select>
      </div>
    </div>
  );
};

type MultipleChoiceConditionType = "is" | "is not" | "is any of";

const SelectMultipleChoiceCondition = <T extends ConditionFields>({
  conditionInput,
  setConditionInput,
  step,
  showIsAnyOf = true,
}: SelectConditionProps<T>) => {
  // Determine current condition type
  const conditionType: MultipleChoiceConditionType = useMemo(() => {
    if (conditionInput.verificationStepMultipleChoiceAnyCondition) {
      return "is any of";
    }
    if (conditionInput.verificationStepMultipleChoiceCondition?.condition) {
      return "is";
    }
    return "is not";
  }, [conditionInput]);

  // Get selected option IDs for "is any of" mode
  const selectedOptionIds = useMemo(() => {
    return (
      conditionInput.verificationStepMultipleChoiceAnyCondition?.options?.map(
        (opt) => opt.stepResponseOptionId,
      ) || []
    );
  }, [conditionInput.verificationStepMultipleChoiceAnyCondition]);

  const onConditionTypeChange = useCallback(
    (value: string) => {
      if (value === "is any of") {
        // Switch to "is any of" mode - clear single option condition, set any condition
        const firstOptionId = step?.responseOptions?.[0]?.id || "";
        setConditionInput({
          ...conditionInput,
          verificationStepMultipleChoiceCondition: undefined,
          verificationStepMultipleChoiceAnyCondition: {
            options: firstOptionId
              ? [{ stepResponseOptionId: firstOptionId }]
              : [],
          },
        });
      } else {
        // Switch to single option mode - clear any condition
        const firstOptionId =
          conditionInput.verificationStepMultipleChoiceCondition
            ?.stepResponseOptionId ||
          step?.responseOptions?.[0]?.id ||
          "";
        setConditionInput({
          ...conditionInput,
          verificationStepMultipleChoiceAnyCondition: undefined,
          verificationStepMultipleChoiceCondition: {
            stepResponseOptionId: firstOptionId,
            condition: value === "is",
          },
        });
      }
    },
    [setConditionInput, conditionInput, step?.responseOptions],
  );

  const onStepResponseOptionChange = useCallback(
    (value: string) => {
      setConditionInput({
        ...conditionInput,
        verificationStepMultipleChoiceCondition: {
          stepResponseOptionId: value,
          condition:
            conditionInput.verificationStepMultipleChoiceCondition?.condition ||
            false,
        },
      });
    },
    [setConditionInput, conditionInput],
  );

  // Get display text for selected options in "is any of" mode
  const selectedOptionsText = useMemo(() => {
    const selectedOptions = step?.responseOptions?.filter((x) =>
      selectedOptionIds.includes(x.id),
    );
    return selectedOptions?.map((x) => x.text?.en).join(", ") || "";
  }, [selectedOptionIds, step?.responseOptions]);

  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-center">
        <Select onValueChange={onConditionTypeChange} value={conditionType}>
          <SelectTrigger className="mr-[8px] w-[136px]">
            <SelectValue>{conditionType}</SelectValue>
          </SelectTrigger>
          <SelectContent portal={false}>
            <SelectItem value="is">is</SelectItem>
            <SelectItem value="is not">is not</SelectItem>
            {showIsAnyOf && (
              <SelectItem value="is any of">is any of</SelectItem>
            )}
          </SelectContent>
        </Select>

        {conditionType !== "is any of" && (
          <Select
            onValueChange={onStepResponseOptionChange}
            value={
              conditionInput.verificationStepMultipleChoiceCondition
                ?.stepResponseOptionId
            }
          >
            <SelectTrigger>
              <SelectValue>
                {
                  step?.responseOptions?.find(
                    (x) =>
                      x.id ===
                      conditionInput.verificationStepMultipleChoiceCondition
                        ?.stepResponseOptionId,
                  )?.text?.en
                }
              </SelectValue>
            </SelectTrigger>
            <SelectContent portal={false}>
              {step?.responseOptions?.map((x) => (
                <SelectItem key={x.id} value={x.id}>
                  {x.text?.en}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}

        {conditionType === "is any of" && (
          <MultiSelect
            options={
              step?.responseOptions?.map((opt) => ({
                label: opt.text?.en || "Untitled option",
                value: opt.id,
              })) || []
            }
            defaultValue={selectedOptionIds}
            onValueChange={(values) => {
              if (values.length === 0) return;
              setConditionInput({
                ...conditionInput,
                verificationStepMultipleChoiceAnyCondition: {
                  options: values.map((id) => ({ stepResponseOptionId: id })),
                },
              });
            }}
            triggerLabel={selectedOptionsText}
            placeholder="Select options"
            enableAll={false}
            hideActions={true}
            className="rounded-lg border border-input bg-background text-sm shadow-sm ring-offset-background"
          />
        )}
      </div>
    </div>
  );
};

export default SelectCondition;
